/**
 * Explicação de falhas em linguagem compreensível, derivada de `last_error`
 * (R19.4), com **orientação de próximo passo** e **sem expor detalhes internos
 * crus**.
 *
 * `last_error` vem de `GET /jobs/{id}` no formato do backend
 * (`makepub_shared.errors.MakePubError.last_error`):
 *   - `"<Code>: <mensagem>"` (ex.: `"ParseError: PDF corrompido"`), ou
 *   - apenas `"<Code>"` (ex.: `"RuntimeTimeout"`).
 *
 * Mapeamos o *code* estável para uma explicação amigável. A mensagem técnica
 * que acompanha o code **não** é exibida ao usuário (pode conter detalhes
 * internos); usamos apenas o code para escolher o texto. Codes desconhecidos
 * caem em uma explicação genérica segura.
 */

/** Explicação amigável de uma falha, pronta para a UI. */
export interface FailureExplanation {
  /** Título curto e claro. */
  readonly title: string;
  /** Explicação em pt-BR, sem jargão. */
  readonly message: string;
  /** Próximo passo sugerido ao usuário (acionável). */
  readonly nextStep: string;
  /** `true` se tentar de novo o mesmo arquivo provavelmente ajuda. */
  readonly retryable: boolean;
}

/** Extrai o *code* estável de uma string `last_error` ("Code: msg" → "Code"). */
export function errorCodeFromLastError(lastError: string | null | undefined): string | null {
  if (!lastError) {
    return null;
  }
  const trimmed = lastError.trim();
  if (trimmed === "") {
    return null;
  }
  const colon = trimmed.indexOf(":");
  const code = colon >= 0 ? trimmed.slice(0, colon) : trimmed;
  return code.trim() || null;
}

const EXPLANATIONS: Record<string, FailureExplanation> = {
  UnsupportedFormat: {
    title: "Formato não suportado",
    message: "O tipo de documento enviado não é aceito para conversão.",
    nextStep: "Envie um arquivo em PDF, Word (.docx), Markdown, texto ou HTML.",
    retryable: false,
  },
  FileTooLarge: {
    title: "Arquivo muito grande",
    message: "O documento ultrapassou o tamanho máximo permitido no seu plano.",
    nextStep:
      "Reduza o tamanho do documento (ou divida em partes) e envie novamente, ou use um plano maior.",
    retryable: false,
  },
  QuotaExceeded: {
    title: "Limite do plano atingido",
    message: "Você já usou todas as conversões disponíveis no seu plano neste mês.",
    nextStep: "Aguarde a renovação mensal do plano ou faça upgrade para continuar convertendo.",
    retryable: false,
  },
  ParseError: {
    title: "Não conseguimos ler o documento",
    message:
      "O arquivo pode estar corrompido, protegido por senha ou em um formato interno inesperado.",
    nextStep:
      "Abra o documento para confirmar que está íntegro, salve/exporte novamente e reenvie.",
    retryable: true,
  },
  GenerationError: {
    title: "Falha ao montar o EPUB",
    message: "Encontramos um problema ao gerar o livro digital a partir do seu conteúdo.",
    nextStep:
      "Tente enviar novamente. Se o problema persistir, simplifique a formatação do documento.",
    retryable: true,
  },
  ValidationBlocked: {
    title: "O EPUB não passou na verificação de qualidade",
    message:
      "O livro gerado não atendeu aos critérios de qualidade e acessibilidade exigidos.",
    nextStep:
      "Revise a estrutura do documento (títulos, imagens com descrição) e tente novamente.",
    retryable: true,
  },
  RepairExhausted: {
    title: "Não foi possível corrigir automaticamente",
    message:
      "Tentamos ajustar o resultado algumas vezes, mas alguns pontos não puderam ser resolvidos sozinhos.",
    nextStep:
      "Revise o documento de origem (estrutura e imagens) e envie novamente. Se persistir, fale com o suporte.",
    retryable: true,
  },
  ToolFailure: {
    title: "Instabilidade temporária",
    message: "Um dos passos da conversão falhou de forma temporária.",
    nextStep: "Aguarde alguns minutos e tente enviar o documento novamente.",
    retryable: true,
  },
  RuntimeTimeout: {
    title: "A conversão demorou demais",
    message: "O processamento do documento excedeu o tempo máximo previsto.",
    nextStep:
      "Documentos muito extensos podem exceder o tempo. Tente dividir o conteúdo e reenviar.",
    retryable: true,
  },
};

/** Explicação genérica para codes desconhecidos ou `last_error` ausente. */
export const GENERIC_FAILURE: FailureExplanation = {
  title: "Não foi possível concluir a conversão",
  message: "Algo deu errado durante o processamento do seu documento.",
  nextStep: "Tente enviar o documento novamente. Se o problema continuar, fale com o suporte.",
  retryable: true,
};

/**
 * Deriva uma explicação amigável a partir de `last_error` (R19.4).
 *
 * Usa apenas o *code* estável; nunca repassa a mensagem técnica crua ao usuário.
 */
export function explainFailure(lastError: string | null | undefined): FailureExplanation {
  const code = errorCodeFromLastError(lastError);
  if (code && code in EXPLANATIONS) {
    return EXPLANATIONS[code];
  }
  return GENERIC_FAILURE;
}
